import { useCallback, useMemo, useRef } from 'react'
import { FormikErrors } from 'formik'
import { useNavigate } from 'react-router-dom'
import { UseData } from './useData'
import { AddLeadProps } from '../MainForm/MainForm'
import { ILeadFormModel, transform } from '../typings'
import { useAppDispatch } from '@user/store/hooks'
import { FormikHelpers } from 'formik'
import { customValuesToInputModel } from '@user/components/CustomFields'
import { toast } from '@sales-center/components'
import { createLead } from '@user/store/lead'
import { validateNoCustomSettings } from '.'
import { setEntityAndCustomErrors } from '@user/components/Settings/EntityFormSettings/utility/setEntityAndCustomErrors'
import {
  addLeadCompany,
  addLeadFirstName,
  addLeadLastName,
} from '@user/components/Settings/EntityFormSettings'
import {
  getNameRequirementMode,
  NAME_REQUIREMENT_OR,
} from '@user/components/Settings/EntityFormSettings/config/nameRequirementMode'
import { selectAllEntityFormSettings } from '@user/store/entityFormSettings'
import { useLocalisedValidations } from '@user/utils/useLocalisedValidations'
import { useRemoveHiddenFieldsFromAPI } from '@user/components/Leads/AddLeadPage/hooks/removeHiddenFieldsFromAPI'
import { ENTITY_TYPES, updateLoadingState } from '@sales-center/common'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { guestRoleName } from '@user/components/Settings/Users/UpdateUser/constants'
import { isAPIError } from '@user/store/services/crud'
import { externalAppTimeoutError } from '@user/store/utilities/functions'
import { usePreviousLocation } from '@user/utils/usePreviousLocation'
import { EntityLocationDTO } from '@user/store/map/typings'
import {
  mergeLeadFormErrors,
  collectAddLeadEmails,
  validateEmails,
  applyInvalidDomainErrors,
} from '@user/store/emailValidation'
import type { IEmailValidationModel } from '@user/store/emailValidation'

interface MapAddressCache {
  street1?: string
  street2?: string
  city?: string
  stateAbbreviation?: string
  postalCode?: string
  latitude?: number
  longitude?: number
}

type Props = Pick<
  UseData,
  | 'setHideOptional'
  | 'customFields'
  | 'disabled'
  | 'isMappedWithUpdateLocationPerm'
  | 'entitySettingsMap'
  | 'requiredEntitySettings'
  | 'customFieldSettings'
  | 'isAddFromLocation'
> &
  Pick<AddLeadProps, 'isDraftLead'> & {
    fromMapView?: boolean
    handleMapAfterAddLeadSaveSuccess?: (newPin: EntityLocationDTO) => void
  }

export function useHandlers(props: Props) {
  const {
    setHideOptional,
    customFields,
    disabled,
    isMappedWithUpdateLocationPerm,
    entitySettingsMap,
    requiredEntitySettings,
    customFieldSettings,
    isAddFromLocation,
    isDraftLead,
    fromMapView,
    handleMapAfterAddLeadSaveSuccess,
  } = props

  const previousLocation = usePreviousLocation()

  const currentUser = useSelector(selectCurrentUser)
  const isGuestUser = currentUser?.roleName === guestRoleName

  const updateCoreLocation = disabled && isMappedWithUpdateLocationPerm

  const allEntityFormSettings = useSelector(selectAllEntityFormSettings)
  const nameRequirementMode = useMemo(
    () => getNameRequirementMode(allEntityFormSettings, ENTITY_TYPES.Lead),
    [allEntityFormSettings],
  )

  const { validatePostalCode } = useLocalisedValidations()
  const { removeHiddenFieldsFromAPI } = useRemoveHiddenFieldsFromAPI(
    entitySettingsMap,
    customFieldSettings,
  )
  const navigate = useNavigate()

  const dispatch = useAppDispatch()

  const forceRevalidateEmailRef = useRef(false)
  const domainResultsRef = useRef<IEmailValidationModel[]>([])
  const lastEmailsKeyRef = useRef<string>('')

  const prepareSubmitValidation = useCallback(() => {
    forceRevalidateEmailRef.current = true
  }, [])

  const onCancel = useCallback(() => {
    if (isDraftLead) {
      navigate('/pending-leads')
    } else if (isAddFromLocation || previousLocation === '/leads/addLead') {
      navigate('/scdashboard')
    } else {
      if (window.history.length > 1) {
        navigate(-1)
      } else {
        window.close()
      }
    }
  }, [navigate, isDraftLead, isAddFromLocation, previousLocation])

  const onChangeHideOptional = useCallback(
    (_e, checked: boolean) => setHideOptional(checked),
    [setHideOptional],
  )

  const onSubmit = useCallback(
    async (model: ILeadFormModel, helpers: FormikHelpers<ILeadFormModel>) => {
      dispatch(updateLoadingState('visible'))
      const viewDetail = model.viewDetail
      if (isGuestUser) model.viewDetail = undefined

      const customFieldValues = customValuesToInputModel(model, customFields)
      const formatted = transform(model, customFieldValues, updateCoreLocation)
      const cleanedLeadData = removeHiddenFieldsFromAPI(formatted)
      const response = await dispatch(createLead(cleanedLeadData))
      dispatch(updateLoadingState('hidden'))

      if (createLead.fulfilled.match(response)) {
        sessionStorage.setItem('pageNumber', '1')
        toast.success(
          model.createNewLocation
            ? 'Lead Saved. New Locations have been created in PestPac.'
            : 'Lead created',
        )
        if (!isGuestUser) {
          helpers.resetForm()
          if (fromMapView && handleMapAfterAddLeadSaveSuccess) {
            const { id, assignee, serviceLocation } = response.payload
            let cachedAddress: MapAddressCache = {}
            try {
              const cached = sessionStorage.getItem('mapAddressCache')
              if (cached) {
                cachedAddress = JSON.parse(cached)
              }
            } catch {
              // Ignore parse errors
            }
            handleMapAfterAddLeadSaveSuccess({
              count: 1,
              entityId: id,
              entityType: ENTITY_TYPES.Lead,
              latitude: cachedAddress.latitude || 0,
              longitude: cachedAddress.longitude || 0,
              multitenant: false,
              serviceLocationId: serviceLocation?.id || 0,
              pinId: `${id}${serviceLocation?.id || ''}`,
              entities: [],
              assigneeId: assignee?.id || undefined,
              street1: cachedAddress.street1 || '',
              street2: cachedAddress.street2 || '',
              city: cachedAddress.city || '',
              stateAbbreviation: cachedAddress.stateAbbreviation || '',
              postalCode: cachedAddress.postalCode || '',
            })
            if (model.viewDetail) {
              window.open(
                `/#/leads/${response.payload.id}`,
                '_blank',
                'noopener,noreferrer',
              )
            }
          } else {
            navigate(
              model.viewDetail ? `/leads/${response.payload.id}` : '/leads',
            )
          }
        } else {
          if (viewDetail) {
            // Guest User clicked "Save and View" - redirect to dashboard
            navigate('/scdashboard')
          } else {
            // Guest User clicked "Save" - reload the page
            navigate('/leads/addLead')
            window.location.reload()
          }
        }
      } else {
        if (isAPIError(response.payload)) {
          const errorMessage = response.payload.Message
          if (errorMessage.toLowerCase().includes('tax code not found')) {
            toast.error(
              `Tax code not found for the provided address. Please ensure that the tax code is configured in  ${currentUser.externalProduct}.`,
            )
          } else if (!externalAppTimeoutError(response.payload)) {
            toast.error(`Unable to create lead. ${response.payload.Message}`)
          }
        } else {
          toast.error(`Unable to create lead.`)
        }
      }
    },
    [
      dispatch,
      isGuestUser,
      customFields,
      updateCoreLocation,
      removeHiddenFieldsFromAPI,
      fromMapView,
      handleMapAfterAddLeadSaveSuccess,
      navigate,
      currentUser.externalProduct,
    ],
  )

  const validate = useCallback(
    async (model: ILeadFormModel): Promise<FormikErrors<ILeadFormModel>> => {
      const { callTrackingActivity } = model

      let errors: FormikErrors<ILeadFormModel> = {
        ...validateNoCustomSettings(
          model,
          requiredEntitySettings,
          entitySettingsMap,
          validatePostalCode,
          nameRequirementMode,
        ),
      }

      // In "or" mode the name group's requiredness is expressed by the
      // section-level `nameGroup` error from `validateNoCustomSettings`
      // above, not by per-field 'Required' errors. Strip the name fields out
      // of what we hand to `setEntityAndCustomErrors` so it can't re-stamp
      // them, without touching that utility's frozen signature/contract.
      const entitySettingsForRequiredCheck =
        nameRequirementMode === NAME_REQUIREMENT_OR
          ? requiredEntitySettings.filter(
              (setting) =>
                setting.fieldId !== addLeadFirstName &&
                setting.fieldId !== addLeadLastName &&
                setting.fieldId !== addLeadCompany,
            )
          : requiredEntitySettings

      errors = setEntityAndCustomErrors(
        entitySettingsForRequiredCheck,
        model,
        errors,
        customFieldSettings,
      )
      //bypassing campaign validation due to call tracking
      if (callTrackingActivity?.callTrackingEventId) {
        if (errors.campaignId) delete errors.campaignId
      }

      if (model.createNewLocation) {
        const emailsToValidate = collectAddLeadEmails(model)
        const emailsKey = emailsToValidate.join('|')

        const needsRevalidation =
          emailsKey !== lastEmailsKeyRef.current ||
          forceRevalidateEmailRef.current
        forceRevalidateEmailRef.current = false

        if (needsRevalidation && emailsToValidate.length > 0) {
          lastEmailsKeyRef.current = emailsKey
          try {
            domainResultsRef.current = await validateEmails(
              dispatch,
              emailsToValidate,
            )
          } catch {
            lastEmailsKeyRef.current = ''
            toast.error('Unable to validate email addresses. Please try again.')
          }
        } else if (emailsToValidate.length === 0) {
          domainResultsRef.current = []
          lastEmailsKeyRef.current = ''
        }

        if (domainResultsRef.current.length > 0) {
          const domainErrors = applyInvalidDomainErrors(
            model,
            domainResultsRef.current,
          )
          if (Object.keys(domainErrors).length > 0) {
            errors = mergeLeadFormErrors(errors, domainErrors)
          }
        }
      } else {
        domainResultsRef.current = []
        lastEmailsKeyRef.current = ''
        forceRevalidateEmailRef.current = false
      }

      return errors
    },
    [
      requiredEntitySettings,
      entitySettingsMap,
      validatePostalCode,
      customFieldSettings,
      dispatch,
      nameRequirementMode,
    ],
  )

  return {
    onCancel,
    onChangeHideOptional,
    onSubmit,
    validate,
    prepareSubmitValidation,
  }
}
